import { getCurrentUser, AuthUser } from 'aws-amplify/auth';
import React, { createContext, useState, useEffect, useContext } from 'react';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// テスト環境では認証状態をモックする
const mockUser: AuthUser = {
  userId: 'test-user-id',
  username: 'test-user',
  signInDetails: {
    loginId: 'test-user',
    authFlowType: 'USER_SRP_AUTH',
  },
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(
    process.env.NODE_ENV === 'test' ? mockUser : null
  );
  const [loading, setLoading] = useState(process.env.NODE_ENV !== 'test');

  const refreshUser = async () => {
    if (process.env.NODE_ENV === 'test') {
      setLoading(false);
      return;
    }
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      setUser(null);
    }
  };

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, []);

  const signOut = async () => {
    if (process.env.NODE_ENV === 'test') {
      setUser(null);
      return;
    }
    const { signOut: amplifySignOut } = await import('aws-amplify/auth');
    await amplifySignOut();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
